import React, { useEffect, useState } from 'react';
import { createContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  strengthRules,
  newPasswordIsDifferFromOldOneValidate,
  StrengthRulesType,
} from '@components-shared/forms/strengthPassword';
import { keyMap, KeyMapKeys } from '@locales/lokalise/keyMap';
import cn from 'classnames';
import styles from './passwordStrengthMeter.styles.module.scss';

export const PasswordStrengthMeterContext = createContext<React.ReactNode | null>(null);

export const PasswordStrengthMeter = React.memo(
  ({
    newPassword,
    oldPassword,
    hasOldPasswordValidation,
  }: {
    newPassword: string;
    oldPassword?: string;
    hasOldPasswordValidation?: boolean;
  }) => {
    const { i18n } = useTranslation();
    const [isTouched, setIsTouched] = useState(false);

    useEffect(() => {
      if (newPassword) {
        setIsTouched(true);
      } else {
        setIsTouched(false);
      }
    }, [newPassword]);

    const getHintClassName = (isValid: boolean) => {
      if (!isTouched) return 'form__hint';
      return `form__hint ${isValid ? styles.valid : styles.notValid}`;
    };

    const strengthMap: Record<keyof StrengthRulesType, { message: KeyMapKeys; isValid: boolean }> = {
      minLength: {
        message: keyMap.formsValidationEightCharactersLong,
        isValid: strengthRules.minLength.fn(newPassword),
      },
      hasUppercase: {
        message: keyMap.formsValidationOneUppercaseLetter,
        isValid: strengthRules.hasUppercase.fn(newPassword),
      },
      hasLowercase: {
        message: keyMap.formsValidationOneLowercaseLetter,
        isValid: strengthRules.hasLowercase.fn(newPassword),
      },
      hasNumber: {
        message: keyMap.formsValidationOneNumber,
        isValid: strengthRules.hasNumber.fn(newPassword),
      },
      hasSpecialCharacters: {
        message: keyMap.formsValidationOneSpecialSymbol,
        isValid: strengthRules.hasSpecialCharacters.fn(newPassword),
      },
      hasRepeatedCharacters: {
        message: keyMap.formsValidationRepeatedCharacters,
        isValid: strengthRules.hasRepeatedCharacters.fn(newPassword),
      },
    };

    const isPasswordDifferentFromOldOne = {
      message: keyMap.formsValidationNewPasswordIsDifferent,
      isValid: newPasswordIsDifferFromOldOneValidate(newPassword, oldPassword),
    };

    return (
      <div>
        <ul className={styles.listContainer}>
          <li className={cn(getHintClassName(strengthMap['minLength'].isValid))}>
            {i18n.t(strengthMap['minLength'].message)}
          </li>
          <li className={cn(getHintClassName(strengthMap['hasUppercase'].isValid))}>
            {i18n.t(strengthMap['hasUppercase'].message)}
          </li>
          <li className={cn(getHintClassName(strengthMap['hasLowercase'].isValid))}>
            {i18n.t(strengthMap['hasLowercase'].message)}
          </li>
          <li className={cn(getHintClassName(strengthMap['hasNumber'].isValid))}>
            {i18n.t(strengthMap['hasNumber'].message)}
          </li>
          <li className={cn(getHintClassName(strengthMap['hasSpecialCharacters'].isValid))}>
            {i18n.t(strengthMap['hasSpecialCharacters'].message)}
          </li>
          <li className={cn(getHintClassName(strengthMap['hasRepeatedCharacters'].isValid))}>
            {i18n.t(strengthMap['hasRepeatedCharacters'].message)}
          </li>
          {hasOldPasswordValidation && (
            <li className={cn(getHintClassName(isPasswordDifferentFromOldOne.isValid))}>
              {i18n.t(isPasswordDifferentFromOldOne.message)}
            </li>
          )}
        </ul>
      </div>
    );
  },
);
